/**
 * Performance Chart Component
 * Displays daily P&L history using Recharts
 */

import { usePerformance } from '../api/client';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function PerformanceChart() {
  const { data, isLoading, error } = usePerformance(30);

  if (isLoading) {
    return (
      <div className="card">
        <div className="loading">Loading performance data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Error loading performance: {error.message}</div>
      </div>
    );
  }

  const performance = data?.performance || [];

  if (performance.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Performance (Last 30 Days)</h2>
        </div>
        <div className="empty-state">
          <div className="empty-state-icon">📈</div>
          <p>No performance data available</p>
        </div>
      </div>
    );
  }

  // Calculate cumulative P&L
  let cumulative = 0;
  const chartData = performance.map((day) => {
    cumulative += day.totalPnL;
    return {
      date: new Date(day.date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      }),
      pnl: day.totalPnL,
      cumulative: cumulative,
      winRate: day.winRate,
    };
  });

  const totalPnL = cumulative;
  const winningDays = performance.filter((d) => d.totalPnL > 0).length;
  const totalDays = performance.length;
  const winRate = totalDays > 0 ? (winningDays / totalDays) * 100 : 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Performance (Last 30 Days)</h2>
        <span className={`card-badge ${totalPnL >= 0 ? 'badge-success' : 'badge-danger'}`}>
          {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
        </span>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '1.5rem' }}>
        <div className="metric">
          <div className="metric-label">Total P&L</div>
          <div className={`metric-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
          </div>
        </div>

        <div className="metric">
          <div className="metric-label">Win Rate</div>
          <div className="metric-value">{winRate.toFixed(1)}%</div>
        </div>

        <div className="metric">
          <div className="metric-label">Trading Days</div>
          <div className="metric-value">
            {winningDays}W / {totalDays - winningDays}L
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={250}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
          <XAxis
            dataKey="date"
            stroke="var(--text-secondary)"
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis
            stroke="var(--text-secondary)"
            style={{ fontSize: '0.75rem' }}
            tickFormatter={(value) => `$${value}`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '0.875rem',
            }}
            formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cumulative P&L']}
          />
          <Line
            type="monotone"
            dataKey="cumulative"
            stroke="var(--accent-blue)"
            strokeWidth={2}
            dot={{ fill: 'var(--accent-blue)', r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
